/**
 * Data Collection Engine - Local-first, schema-driven data management.
 *
 * Provides:
 * - Collections (tables) with typed fields
 * - Validation rules per field
 * - Default values
 * - Relationships (one-to-many, many-to-one)
 * - CRUD operations
 * - Query/filter/sort
 * - Observable changes
 *
 * This is NOT a database. It's a portable data abstraction that:
 * - Runs identically in preview and publish
 * - Can later sync to cloud backends
 * - Is fully serializable
 *
 * Extends: NuvraSchema models, persistenceEngine, eventBus
 */

import type { SchemaModel, SchemaField, SchemaRelation, SchemaValue } from "@/core/schema/nuvraSchema";
import { persistSave, persistLoad } from "@/persistence/persistenceEngine";
import { permissionEngine } from "./permissionEngine";
import { eventBus } from "@/runtime/eventBus";
import { logger } from "@/system/logger";

// ─── Types ───

export interface CollectionRecord {
  _id: string;
  _createdAt: number;
  _updatedAt: number;
  [key: string]: SchemaValue | number;
}

export interface CollectionQuery {
  where?: Record<string, QueryCondition>;
  orderBy?: { field: string; direction: "asc" | "desc" };
  limit?: number;
  offset?: number;
}

export type QueryCondition =
  | { op: "eq"; value: SchemaValue }
  | { op: "neq"; value: SchemaValue }
  | { op: "gt"; value: number }
  | { op: "lt"; value: number }
  | { op: "gte"; value: number }
  | { op: "lte"; value: number }
  | { op: "contains"; value: string }
  | { op: "in"; value: SchemaValue[] };

export interface ValidationError {
  field: string;
  message: string;
  rule: string;
}

export interface MutationResult {
  success: boolean;
  record?: CollectionRecord;
  errors?: ValidationError[];
}

/**
 * Believable past timestamps for a SEEDED sample row, so the generated business
 * reads as one that was already operating. Honored only on the seed path
 * (skipPermissions); real user inserts always get the live clock.
 */
export interface SeedMeta {
  createdAt: number;
  updatedAt: number;
}

type CollectionListener = (event: "insert" | "update" | "delete", record: CollectionRecord) => void;

// ─── Cross-entity rule hook ─────────────────────────────────────────────────
// A pluggable evaluator for cross-entity (business) validation, run on every insert/update
// after field + unique checks. Decoupled by design: dataEngine never imports the rule engine;
// the rule engine registers itself via setRuleEvaluator(). Returning a non-empty error array
// blocks the write, exactly like a field validation failure. When unset, behaviour is
// byte-identical to before.
export type RuleEvaluator = (collection: string, record: CollectionRecord) => ValidationError[];
let ruleEvaluator: RuleEvaluator | null = null;
export function setRuleEvaluator(fn: RuleEvaluator | null): void { ruleEvaluator = fn; }

// ─── Collection ───

class Collection {
  private records = new Map<string, CollectionRecord>();
  private listeners = new Set<CollectionListener>();
  private indexes = new Map<string, Map<string, Set<string>>>(); // fieldName → value → recordIds
  private nextId = 1;

  constructor(
    public readonly name: string,
    public readonly fields: SchemaField[],
    public readonly relations: SchemaRelation[],
  ) {
    // Build indexes for indexed fields
    for (const field of fields) {
      if (field.indexed || field.unique) {
        this.indexes.set(field.name, new Map());
      }
    }
  }

  /**
   * Insert a new record with validation.
   */
  insert(data: Record<string, SchemaValue>, skipPermissions = false, seedMeta?: SeedMeta): MutationResult {
    // Permission check
    if (!skipPermissions) {
      const access = permissionEngine.checkCollectionAccess(this.name, "create");
      if (!access.allowed) return { success: false, errors: [{ field: "_permission", message: access.reason || "Permission denied", rule: "permission" }] };
    }

    const errors = this.validate(data);
    if (errors.length > 0) return { success: false, errors };

    // Unique constraint check
    const uniqueErrors = this.checkUniqueConstraints(data);
    if (uniqueErrors.length > 0) return { success: false, errors: uniqueErrors };

    const now = Date.now();
    // Seeded sample data represents a business that was already operating, so its
    // rows carry believable PAST timestamps. This is honored ONLY on the seed path
    // (skipPermissions): a real user insert can never backdate its own history.
    const useSeed = !!seedMeta && skipPermissions;
    const record: CollectionRecord = {
      _id: `${this.name}_${this.nextId++}_${now.toString(36)}`,
      _createdAt: useSeed ? seedMeta!.createdAt : now,
      _updatedAt: useSeed ? seedMeta!.updatedAt : now,
    };

    // Apply defaults and values
    for (const field of this.fields) {
      if (data[field.name] !== undefined) {
        record[field.name] = data[field.name];
      } else if (field.defaultValue !== undefined) {
        record[field.name] = field.defaultValue;
      } else {
        record[field.name] = null;
      }
    }

    // Cross-entity (business) rules - evaluated on the fully-built record before commit.
    if (ruleEvaluator) {
      const ruleErrors = ruleEvaluator(this.name, record);
      if (ruleErrors.length > 0) return { success: false, errors: ruleErrors };
    }

    this.records.set(record._id, record);
    this.updateIndexes(record, "add");
    this.notify("insert", record);

    return { success: true, record };
  }

  /**
   * Update an existing record by ID.
   */
  update(id: string, data: Partial<Record<string, SchemaValue>>, skipPermissions = false): MutationResult {
    if (!skipPermissions) {
      const access = permissionEngine.checkCollectionAccess(this.name, "update");
      if (!access.allowed) return { success: false, errors: [{ field: "_permission", message: access.reason || "Permission denied", rule: "permission" }] };
    }

    const existing = this.records.get(id);
    if (!existing) return { success: false, errors: [{ field: "_id", message: "Record not found", rule: "exists" }] };

    // Validate only provided fields
    const errors = this.validatePartial(data);
    if (errors.length > 0) return { success: false, errors };

    // Unique constraint check (excluding current record)
    const uniqueErrors = this.checkUniqueConstraints(data as Record<string, SchemaValue>, id);
    if (uniqueErrors.length > 0) return { success: false, errors: uniqueErrors };

    const updated: CollectionRecord = { ...existing, ...data, _updatedAt: Date.now() };

    // Cross-entity (business) rules - evaluated on the post-update record before commit, so a
    // rule like "a deal cannot be won without an activity" sees the real related rows.
    if (ruleEvaluator) {
      const ruleErrors = ruleEvaluator(this.name, updated);
      if (ruleErrors.length > 0) return { success: false, errors: ruleErrors };
    }

    this.updateIndexes(existing, "remove");
    this.records.set(id, updated);
    this.updateIndexes(updated, "add");
    this.notify("update", updated);

    return { success: true, record: updated };
  }

  /**
   * Delete a record by ID.
   */
  delete(id: string, skipPermissions = false): boolean {
    if (!skipPermissions) {
      const access = permissionEngine.checkCollectionAccess(this.name, "delete");
      if (!access.allowed) return false;
    }

    const record = this.records.get(id);
    if (!record) return false;

    this.updateIndexes(record, "remove");
    this.records.delete(id);
    this.notify("delete", record);
    return true;
  }

  /**
   * Get a single record by ID.
   */
  getById(id: string): CollectionRecord | null {
    return this.records.get(id) ?? null;
  }

  /**
   * Query records with filters, sorting, pagination.
   */
  query(q: CollectionQuery = {}): CollectionRecord[] {
    let results = [...this.records.values()];

    // Where clauses
    if (q.where) {
      for (const [field, condition] of Object.entries(q.where)) {
        results = results.filter(r => matchCondition(r[field] as SchemaValue, condition));
      }
    }

    // Order by
    if (q.orderBy) {
      const { field, direction } = q.orderBy;
      results.sort((a, b) => {
        const av = a[field];
        const bv = b[field];
        if (av === bv) return 0;
        if (av === null || av === undefined) return 1;
        if (bv === null || bv === undefined) return -1;
        const cmp = av < bv ? -1 : 1;
        return direction === "desc" ? -cmp : cmp;
      });
    }

    // Pagination
    if (q.offset) results = results.slice(q.offset);
    if (q.limit) results = results.slice(0, q.limit);

    return results;
  }

  /**
   * Count records matching optional query.
   */
  count(q?: CollectionQuery): number {
    if (!q?.where) return this.records.size;
    return this.query(q).length;
  }

  /**
   * Get all records.
   */
  all(): CollectionRecord[] {
    return [...this.records.values()];
  }

  /**
   * Clear all records.
   */
  clear(): void {
    this.records.clear();
    for (const idx of this.indexes.values()) idx.clear();
    this.nextId = 1;
  }

  /**
   * Subscribe to collection changes.
   */
  onChange(listener: CollectionListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Export all records (for serialization).
   */
  export(): CollectionRecord[] {
    return [...this.records.values()];
  }

  /**
   * Import records (for restore).
   */
  import(records: CollectionRecord[]): void {
    this.records.clear();
    for (const r of records) {
      this.records.set(r._id, r);
    }
    if (records.length > 0) {
      this.nextId = Math.max(...records.map(r => {
        const parts = r._id.split("_");
        return parseInt(parts[1]) || 0;
      })) + 1;
    }
  }

  // ─── Validation ───

  private validate(data: Record<string, SchemaValue>): ValidationError[] {
    const errors: ValidationError[] = [];

    for (const field of this.fields) {
      const value = data[field.name];

      // Required check
      if (field.required && (value === undefined || value === null || value === "")) {
        errors.push({ field: field.name, message: `${field.name} is required`, rule: "required" });
        continue;
      }

      if (value === undefined || value === null) continue;

      errors.push(...this.validateField(field, value));
    }

    return errors;
  }

  /**
   * Validate a single field's value against its full rule set (type, enum, email,
   * url, min/max, pattern). Shared by insert (validate) and update (validatePartial)
   * so an invalid update is rejected exactly like an invalid create.
   */
  private validateField(field: SchemaField, value: SchemaValue): ValidationError[] {
    const errors: ValidationError[] = [];

    // Type check
    if (!this.checkFieldType(value, field.type)) {
      errors.push({ field: field.name, message: `${field.name} must be ${field.type}`, rule: "type" });
    }

    // Enum check
    if (field.type === "enum" && field.enumValues && typeof value === "string") {
      if (!field.enumValues.includes(value)) {
        errors.push({ field: field.name, message: `${field.name} must be one of: ${field.enumValues.join(", ")}`, rule: "enum" });
      }
    }

    // Email check
    if (field.type === "email" && typeof value === "string") {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        errors.push({ field: field.name, message: `${field.name} must be a valid email`, rule: "email" });
      }
    }

    // URL check
    if (field.type === "url" && typeof value === "string") {
      try { new URL(value); } catch {
        errors.push({ field: field.name, message: `${field.name} must be a valid URL`, rule: "url" });
      }
    }

    // Min/max for numbers
    if (typeof value === "number") {
      if (field.min !== undefined && value < field.min) {
        errors.push({ field: field.name, message: `${field.name} must be >= ${field.min}`, rule: "min" });
      }
      if (field.max !== undefined && value > field.max) {
        errors.push({ field: field.name, message: `${field.name} must be <= ${field.max}`, rule: "max" });
      }
    }

    // Min/max for string length
    if (typeof value === "string" && (field.type === "string" || field.type === "email" || field.type === "url")) {
      if (field.min !== undefined && value.length < field.min) {
        errors.push({ field: field.name, message: `${field.name} must be at least ${field.min} characters`, rule: "minLength" });
      }
      if (field.max !== undefined && value.length > field.max) {
        errors.push({ field: field.name, message: `${field.name} must be at most ${field.max} characters`, rule: "maxLength" });
      }
    }

    // Pattern validation
    if (field.pattern && typeof value === "string") {
      try {
        if (!new RegExp(field.pattern).test(value)) {
          errors.push({ field: field.name, message: `${field.name} does not match required pattern`, rule: "pattern" });
        }
      } catch { /* invalid regex - skip */ }
    }

    return errors;
  }

  /**
   * Validate a partial update. Each PROVIDED field is checked with the full rule set
   * (same as insert); a required field explicitly set to empty/null is rejected.
   * Fields not present in the update are left untouched (true partial update).
   */
  private validatePartial(data: Partial<Record<string, SchemaValue>>): ValidationError[] {
    const errors: ValidationError[] = [];
    for (const [name, value] of Object.entries(data)) {
      const field = this.fields.find(f => f.name === name);
      if (!field) continue;
      if (field.required && (value === undefined || value === null || value === "")) {
        errors.push({ field: name, message: `${name} is required`, rule: "required" });
        continue;
      }
      if (value === undefined || value === null) continue;
      errors.push(...this.validateField(field, value));
    }
    return errors;
  }

  private checkFieldType(value: SchemaValue, type: string): boolean {
    switch (type) {
      case "string": case "email": case "url": case "enum": return typeof value === "string";
      case "number": return typeof value === "number";
      case "boolean": return typeof value === "boolean";
      case "date": return typeof value === "string" || typeof value === "number";
      case "json": return typeof value === "object";
      case "reference": return typeof value === "string";
      default: return true;
    }
  }

  private notify(event: "insert" | "update" | "delete", record: CollectionRecord): void {
    for (const fn of this.listeners) {
      try { fn(event, record); } catch (e) {
        logger.error("Collection listener error", { collection: this.name, error: e });
      }
    }
  }

  // ─── Unique Constraints ───

  private checkUniqueConstraints(data: Record<string, SchemaValue>, excludeId?: string): ValidationError[] {
    const errors: ValidationError[] = [];
    for (const field of this.fields) {
      if (!field.unique) continue;
      const value = data[field.name];
      if (value === undefined || value === null) continue;

      for (const [id, record] of this.records) {
        if (id === excludeId) continue;
        if (record[field.name] === value) {
          errors.push({ field: field.name, message: `${field.name} must be unique - "${value}" already exists`, rule: "unique" });
          break;
        }
      }
    }
    return errors;
  }

  // ─── Index Management ───

  private updateIndexes(record: CollectionRecord, action: "add" | "remove"): void {
    for (const [fieldName, index] of this.indexes) {
      const value = String(record[fieldName] ?? "");
      if (action === "add") {
        if (!index.has(value)) index.set(value, new Set());
        index.get(value)!.add(record._id);
      } else {
        index.get(value)?.delete(record._id);
        if (index.get(value)?.size === 0) index.delete(value);
      }
    }
  }
}

// ─── Query Matcher ───

function matchCondition(value: SchemaValue, condition: QueryCondition): boolean {
  switch (condition.op) {
    case "eq": return value === condition.value;
    case "neq": return value !== condition.value;
    case "gt": return typeof value === "number" && value > condition.value;
    case "lt": return typeof value === "number" && value < condition.value;
    case "gte": return typeof value === "number" && value >= condition.value;
    case "lte": return typeof value === "number" && value <= condition.value;
    case "contains": return typeof value === "string" && value.includes(condition.value);
    case "in": return condition.value.includes(value);
    default: return true;
  }
}

// ─── Data Engine (Collection Manager) ───

class DataEngine {
  private collections = new Map<string, Collection>();
  private projectId: string | null = null;

  /**
   * Initialize from NuvraSchema models.
   */
  initFromSchema(projectId: string, models: SchemaModel[]): void {
    this.projectId = projectId;
    this.collections.clear();

    for (const model of models) {
      const collection = new Collection(model.name, model.fields, model.relations || []);
      this.collections.set(model.name, collection);
    }

    // Restore persisted data
    this.restoreFromStorage();

    logger.info("DataEngine initialized", { projectId, collections: models.length });
  }

  /**
   * Get a collection by name.
   */
  collection(name: string): Collection | null {
    return this.collections.get(name) ?? null;
  }

  /**
   * List all collection names.
   */
  collectionNames(): string[] {
    return [...this.collections.keys()];
  }

  /**
   * Persist all collection data to storage.
   */
  persist(): void {
    if (!this.projectId) return;
    const data: Record<string, CollectionRecord[]> = {};
    for (const [name, col] of this.collections) {
      data[name] = col.export();
    }
    persistSave(`data-${this.projectId}`, data);
    logger.debug("DataEngine persisted", { collections: Object.keys(data).length });
  }

  /**
   * Restore collection data from storage.
   */
  private restoreFromStorage(): void {
    if (!this.projectId) return;
    const data = persistLoad<Record<string, CollectionRecord[]>>(`data-${this.projectId}`);
    if (!data) return;

    for (const [name, records] of Object.entries(data)) {
      const col = this.collections.get(name);
      if (col) col.import(records);
    }
    logger.debug("DataEngine restored from storage");
  }

  /**
   * Export all data (for snapshots).
   */
  exportAll(): Record<string, CollectionRecord[]> {
    const result: Record<string, CollectionRecord[]> = {};
    for (const [name, col] of this.collections) {
      result[name] = col.export();
    }
    return result;
  }

  /**
   * Import all data (for restore/replay).
   */
  importAll(data: Record<string, CollectionRecord[]>): void {
    for (const [name, records] of Object.entries(data)) {
      const col = this.collections.get(name);
      if (col) col.import(records);
    }
  }

  /**
   * Teardown - clear all collections.
   */
  teardown(): void {
    this.collections.clear();
    this.projectId = null;
  }
}

export const dataEngine = new DataEngine();
export { Collection };
